import { useState, useRef, useEffect } from 'react'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import { createAgentSocket, type AgentEvent } from '@/lib/ws'
import {
  Send, Bot, Wrench, AlertCircle, CheckCircle,
  ChevronDown, ChevronRight, Copy, Check, Zap, Trash2,
} from 'lucide-react'

type Message = {
  id: number
  role: 'user' | 'assistant' | 'tool'
  content: string
  toolName?: string
  toolInput?: Record<string, unknown>
  status?: 'streaming' | 'done' | 'error'
}

type Template = {
  label: string
  prompt: string
  category: string
}

const TEMPLATES: Template[] = [
  {
    label: '500 Error Investigation',
    prompt: '本番環境(PROD)のtwp-backendで直近1時間の500エラーを調査してください。エラーの件数、パターン、原因の推測をお願いします。',
    category: 'Error',
  },
  {
    label: 'Recent Deployments',
    prompt: '直近24時間のデプロイ状況を確認してください。各リポジトリの最新ワークフロー実行結果と、ECSサービスの最終デプロイ時刻を比較してください。',
    category: 'Deploy',
  },
  {
    label: 'Service Health Check',
    prompt: 'PRODとDEV両環境のECSサービス稼働状況を確認してください。異常がある場合はCloudWatchログも合わせて調査してください。',
    category: 'Health',
  },
  {
    label: 'Log Pattern Analysis',
    prompt: '本番環境の全ロググループから直近3時間のERRORとWARNINGを検索し、頻出パターンと影響度を分析してください。',
    category: 'Logs',
  },
  {
    label: 'CI/CD Failures',
    prompt: 'GitHub Actionsで失敗しているワークフローを調査してください。失敗原因と推奨される対処法を教えてください。',
    category: 'CI/CD',
  },
  {
    label: 'Code Search',
    prompt: 'twp-backendのソースコードで、直近のエラーに関連するコードを検索してください。エラーハンドリングの改善点があれば提案してください。',
    category: 'Code',
  },
]

export function Agent() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [connected, setConnected] = useState(false)
  const [running, setRunning] = useState(false)
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set())
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const socketRef = useRef<ReturnType<typeof createAgentSocket> | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(0)

  useEffect(() => {
    return () => socketRef.current?.close()
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const handleEvent = (event: AgentEvent) => {
    setMessages((prev) => {
      const updated = [...prev]

      if (event.type === 'text') {
        const last = updated[updated.length - 1]
        if (last?.role === 'assistant' && last.status === 'streaming') {
          last.content += event.content ?? ''
        } else {
          updated.push({
            id: ++idRef.current,
            role: 'assistant',
            content: event.content ?? '',
            status: 'streaming',
          })
        }
      } else if (event.type === 'tool_use') {
        // Mark any previous streaming assistant message as done before tool call
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'assistant' && updated[i].status === 'streaming') {
            updated[i].status = 'done'
            break
          }
        }
        updated.push({
          id: ++idRef.current,
          role: 'tool',
          content: '',
          toolName: event.name,
          toolInput: event.input,
          status: 'streaming',
        })
      } else if (event.type === 'tool_result') {
        // Find the last streaming tool message with matching name and update it
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'tool' && updated[i].status === 'streaming' && updated[i].toolName === event.name) {
            updated[i].content = event.result ?? ''
            updated[i].status = 'done'
            break
          }
        }
      } else if (event.type === 'done') {
        // Mark ALL remaining streaming messages as done
        for (const msg of updated) {
          if (msg.status === 'streaming') {
            msg.status = 'done'
          }
        }
        setRunning(false)
      } else if (event.type === 'error') {
        // Mark ALL remaining streaming messages as done
        for (const msg of updated) {
          if (msg.status === 'streaming') {
            msg.status = 'done'
          }
        }
        updated.push({
          id: ++idRef.current,
          role: 'assistant',
          content: event.content ?? 'Unknown error',
          status: 'error',
        })
        setRunning(false)
      }

      return updated
    })
  }

  const send = (prompt?: string) => {
    const text = (prompt ?? input).trim()
    if (!text || running) return

    setInput('')
    setRunning(true)

    setMessages((prev) => [
      ...prev,
      { id: ++idRef.current, role: 'user', content: text },
    ])

    if (!socketRef.current || socketRef.current.ws.readyState !== WebSocket.OPEN) {
      const socket = createAgentSocket(
        handleEvent,
        () => {
          setConnected(false)
          setRunning(false)
        },
      )
      socketRef.current = socket
      socket.ws.addEventListener('open', () => {
        setConnected(true)
        socket.send(text)
      })
    } else {
      socketRef.current.send(text)
    }
  }

  const toggleTool = (id: number) => {
    setExpandedTools((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const copyContent = (text: string, id: number) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const clearMessages = () => {
    setMessages([])
    socketRef.current?.close()
    socketRef.current = null
    setConnected(false)
    setRunning(false)
  }

  const formatToolContent = (content: string) => {
    try {
      const parsed = JSON.parse(content)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return content
    }
  }

  return (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">AI Agent</h2>
          <span
            className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-gray-500'}`}
            title={connected ? 'Connected' : 'Disconnected'}
          />
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearMessages}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] transition-colors"
          >
            <Trash2 size={12} />
            Clear
          </button>
        )}
      </div>

      {/* Chat container - direct div instead of Card to keep flex chain intact */}
      <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
        {/* Scrollable messages area */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto space-y-3 p-3">
          {messages.length === 0 && (
            <div className="py-8">
              <div className="text-center text-[var(--color-text-muted)] mb-6">
                <Bot size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">TWP 運用調査エージェント</p>
                <p className="text-xs mt-1">CloudWatch Logs, ECS, GitHub Actions, ソースコードを横断調査</p>
              </div>

              {/* Templates */}
              <div className="max-w-2xl mx-auto">
                <p className="text-xs text-[var(--color-text-muted)] mb-2 flex items-center gap-1">
                  <Zap size={10} />
                  Quick Start
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.label}
                      onClick={() => send(t.prompt)}
                      className="text-left p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors"
                    >
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium">
                        {t.category}
                      </span>
                      <p className="text-sm mt-1.5">{t.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-[var(--color-primary)] text-white p-3'
                    : msg.role === 'tool'
                    ? 'bg-[var(--color-bg)] border border-[var(--color-border)] p-2'
                    : msg.status === 'error'
                    ? 'bg-red-500/10 border border-red-500/30 text-red-400 p-3'
                    : 'bg-[var(--color-bg)] p-3'
                }`}
              >
                {/* Tool header */}
                {msg.role === 'tool' && (
                  <div
                    className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer select-none"
                    onClick={() => toggleTool(msg.id)}
                  >
                    {msg.status === 'done' ? (
                      expandedTools.has(msg.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />
                    ) : (
                      <div className="w-3 h-3 border border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                    )}
                    <Wrench size={12} />
                    <span className="font-mono">{msg.toolName}</span>
                    {msg.status === 'done' && <CheckCircle size={12} className="text-green-400 ml-auto" />}
                    {msg.status === 'done' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          copyContent(msg.content, msg.id)
                        }}
                        className="ml-1 hover:text-[var(--color-text)]"
                        title="Copy result"
                      >
                        {copiedId === msg.id ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                      </button>
                    )}
                  </div>
                )}

                {/* Tool input (collapsed by default) */}
                {msg.role === 'tool' && msg.toolInput && expandedTools.has(msg.id) && (
                  <pre className="mt-2 p-2 rounded bg-[var(--color-bg-elevated)] text-[10px] font-mono overflow-auto max-h-24 text-[var(--color-text-muted)]">
                    {JSON.stringify(msg.toolInput, null, 2)}
                  </pre>
                )}

                {/* Tool result */}
                {msg.role === 'tool' && msg.status === 'done' && expandedTools.has(msg.id) && (
                  <pre className="mt-2 p-2 rounded bg-[var(--color-bg-elevated)] text-xs font-mono whitespace-pre-wrap break-all overflow-auto max-h-64 text-[var(--color-text)]">
                    {formatToolContent(msg.content)}
                  </pre>
                )}

                {/* Error icon */}
                {msg.status === 'error' && (
                  <div className="flex items-center gap-1.5 mb-1 text-xs">
                    <AlertCircle size={12} />
                    Error
                  </div>
                )}

                {/* User message */}
                {msg.role === 'user' && (
                  <pre className="whitespace-pre-wrap break-words font-sans">
                    {msg.content}
                  </pre>
                )}

                {/* Assistant message with markdown */}
                {msg.role === 'assistant' && (
                  <MarkdownRenderer
                    content={msg.content}
                    isStreaming={msg.status === 'streaming'}
                  />
                )}
              </div>
            </div>
          ))}

          {running && (
            <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-sm">
              <div className="animate-spin w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full" />
              Investigating...
            </div>
          )}
        </div>

        {/* Input - pinned at bottom of chat container */}
        <div className="shrink-0 border-t border-[var(--color-border)] p-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="調査指示を入力... (例: 本番のtwp-backendで直近1時間のエラーを調べて)"
              disabled={running}
              className="flex-1 px-4 py-2.5 text-sm rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] disabled:opacity-50"
            />
            <button
              onClick={() => send()}
              disabled={running || !input.trim()}
              className="px-4 py-2.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
